import React, { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import type { ClientId } from "../domain/types";
import {
  CO_BASE,
  findMainStepForSlug,
  isSlugReachable,
  statusForMainStep,
  visibleMainSteps,
  isBooksConnected,
  CoStepStatus,
  CoSubStep,
} from "../domain/steps";
import {
  businessNatureCategoryLabel,
  coTenantDomain,
  SERVICE_MODULE_LABEL,
  TALLY_CONNECTION_KEY,
  ZOHO_CONNECTION_KEY,
} from "../domain/constants";
import type { TallyConnectionSummary, ZohoConnectionSummary } from "../domain/constants";
import { TEAM_PREVIEW_KEY } from "../domain/team";
import type { TeamPreviewEntry } from "../domain/team";
import { APPROVALS_PREVIEW_KEY } from "../domain/approvals";
import type { ApprovalsPreview } from "../domain/approvals";
import { ESCALATIONS_PREVIEW_KEY } from "../domain/escalations";
import type { EscalationsPreview } from "../domain/escalations";
import { persistAnswer, useCoAnswer, useCoClient, useCoProgress } from "../data/store";
import { tallyBooksApi } from "../data/tallyBooksApi";
import { Callout } from "../components/Callout";
import { cn } from "../components/cn";
import { CheckIcon } from "../components/icons";
import {
  BrowserMockup,
  MockupSkeletonCircle,
  MockupSkeletonLine,
  MockupSkeletonPill,
  MockupStatusDot,
} from "../components/BrowserMockup";
import "../styles.css";

const STATUS_GLYPH: Record<CoStepStatus, string> = {
  done: "✓",
  "in-progress": "●",
  "not-started": "○",
};

const STATUS_STYLE: Record<CoStepStatus, string> = {
  done: "text-muted-foreground",
  "in-progress": "text-primary",
  "not-started": "text-muted-foreground/60",
};

function LeftNav({ clientId, activeSlug }: { clientId: ClientId; activeSlug: string | undefined }) {
  const navigate = useNavigate();
  const client = useCoClient(clientId);
  const progress = useCoProgress(clientId);
  // Only explicit user clicks live in state — the default (open iff this is
  // the active group) is derived at render time.
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});

  if (!client) return null;

  const activeMainStep = findMainStepForSlug(client, progress.currentSlug);

  function isOpen(stepId: string): boolean {
    return overrides[stepId] ?? stepId === (activeMainStep ? activeMainStep.id : undefined);
  }

  function toggle(stepId: string) {
    setOverrides((current) => ({ ...current, [stepId]: !isOpen(stepId) }));
  }

  function openSubStep(sub: CoSubStep) {
    navigate(`${CO_BASE}/${clientId}/${sub.slug}`);
  }

  return (
    <nav className="flex min-h-0 w-60 shrink-0 flex-col gap-1 overflow-y-auto border-r p-4">
      {visibleMainSteps(client).map((step) => {
        const status = statusForMainStep(step, progress);
        const open = isOpen(step.id);
        return (
          <div key={step.id} className="flex flex-col gap-0.5">
            <button
              type="button"
              onClick={() => toggle(step.id)}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm font-medium hover:bg-muted/40"
            >
              <span className={cn("w-3.5 shrink-0 text-xs", STATUS_STYLE[status])}>{STATUS_GLYPH[status]}</span>
              <span className="flex-1">{step.title}</span>
            </button>

            {status === "done" && !open ? null : open ? (
              <ul className="ml-5 flex flex-col gap-0.5 border-l pl-3">
                {step.subSteps.map((sub) => {
                  const isActive = sub.slug === activeSlug;
                  const isDone = progress.doneSlugs.includes(sub.slug);
                  // A step ahead of the flow stays locked until Continue
                  // reaches it, so nobody jumps past required answers.
                  const reachable = client ? isSlugReachable(client, progress, sub.slug) : false;
                  return (
                    <li key={sub.slug}>
                      <button
                        type="button"
                        onClick={reachable ? () => openSubStep(sub) : undefined}
                        aria-disabled={!reachable || undefined}
                        title={reachable ? undefined : "Finish the earlier steps first"}
                        className={cn(
                          "flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-sm",
                          isActive
                            ? "bg-muted font-medium text-foreground"
                            : reachable
                              ? "text-muted-foreground hover:bg-muted/40"
                              : "cursor-default text-muted-foreground/50"
                        )}
                      >
                        {isDone && <CheckIcon className="size-3 shrink-0" />}
                        <span>{sub.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        );
      })}
    </nav>
  );
}

function reachedMainStep(
  stepIds: { id: string; slugs: string[] }[],
  stepId: string,
  doneSlugs: string[],
  currentSlug: string
): boolean {
  const step = stepIds.find((candidate) => candidate.id === stepId);
  if (!step) return false;
  return step.slugs.some((slug) => slug === currentSlug || doneSlugs.includes(slug));
}

// The right panel: a minimalistic browser-frame mockup of the client's
// Zournal workspace, built up section by section as onboarding steps
// complete. Read-only and reactive to the same store the middle-panel steps
// write to — every section renders real content once its step is reached, a
// skeleton placeholder before that, so the page always looks like it's
// forming rather than empty.
function RightPreview({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const progress = useCoProgress(clientId);
  const zohoConnection = useCoAnswer<ZohoConnectionSummary>(clientId, ZOHO_CONNECTION_KEY);
  const tallyConnection = useCoAnswer<TallyConnectionSummary>(clientId, TALLY_CONNECTION_KEY);
  const teamPreview = useCoAnswer<TeamPreviewEntry[]>(clientId, TEAM_PREVIEW_KEY);
  const approvalsPreview = useCoAnswer<ApprovalsPreview>(clientId, APPROVALS_PREVIEW_KEY);
  const escalationsPreview = useCoAnswer<EscalationsPreview>(clientId, ESCALATIONS_PREVIEW_KEY);
  const tallyCompanyName = client?.tallyCompanyName ?? "";
  const tallyGstinValue = client?.tallyGstin ?? "";
  const isTallyClient = client?.backend.system === "tally";

  // Connections finished before this preview learned to read Tally banks have
  // the company on the client and no bank count. One read fills that in; later
  // visits already have the answer.
  useEffect(() => {
    if (!isTallyClient || tallyCompanyName.trim() === "") return;
    if (tallyConnection?.bankAccountCount !== undefined) return;
    let live = true;
    tallyBooksApi
      .bankState(clientId)
      .then((state) => {
        if (!live) return;
        void persistAnswer<TallyConnectionSummary>(clientId, TALLY_CONNECTION_KEY, {
          companyName: tallyCompanyName,
          gstin: tallyGstinValue.trim() || null,
          bankAccountCount: state.banks.length,
        });
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [clientId, isTallyClient, tallyCompanyName, tallyConnection?.bankAccountCount, tallyGstinValue]);

  if (!client) return null;

  const stepIds = visibleMainSteps(client).map((step) => ({
    id: step.id,
    slugs: step.subSteps.map((sub) => sub.slug),
  }));
  const done = progress.doneSlugs;

  const booksConnected = isBooksConnected(client);
  const isLive = Boolean(client.liveAt);
  const isTally = client.backend.system === "tally";
  // The connection itself, not the step. Zoho records the organisation the
  // moment the API confirms it. Tally records the open company the same way,
  // so the preview stops saying "Not yet connected" when that name arrives.
  const tallyCompany = client.tallyCompanyName.trim();
  const tallyGstin = client.tallyGstin.trim();
  const connected = isLive || (isTally ? tallyCompany !== "" : Boolean(zohoConnection?.organizationId));
  const booksLabel = isTally ? "Tally" : client.backend.system === "zoho" ? "Zoho Books" : client.backend.system;
  const connectedTo = isTally ? tallyCompany : zohoConnection?.organizationName || "";
  const readGstins = isTally ? (tallyGstin ? 1 : 0) : (zohoConnection?.gstinCount ?? 0);
  const readBanks = isTally ? (tallyConnection?.bankAccountCount ?? 0) : (zohoConnection?.bankAccountCount ?? 0);
  const gstinText = isTally ? tallyGstin : zohoConnection?.gstin;

  const showPlanTag = client.backend.system === "zoho" && client.services.includes("books");
  const connectReached = reachedMainStep(stepIds, "connect-books", done, progress.currentSlug);
  const teamReached = reachedMainStep(stepIds, "team-rules", done, progress.currentSlug);
  const goLiveReached = reachedMainStep(stepIds, "go-live", done, progress.currentSlug);
  const gstinsOrBanksReached = done.includes(booksConnected ? "gst-review" : "readback-review")
    || done.includes("banks-review")
    || progress.currentSlug === "banks-review";

  return (
    <aside className="flex min-h-0 w-80 shrink-0 flex-col gap-3 overflow-y-auto border-l p-5">
      <div>
        <p className="text-xs font-medium text-muted-foreground">Zournal workspace preview</p>
        <p className="text-xs text-muted-foreground/70">Read-only - fills in as steps complete.</p>
      </div>

      <BrowserMockup url={`${client.subdomain || "your-client"}.${coTenantDomain(client.tenantDomain)}`}>
        <div className="flex flex-col gap-2 border-b pb-3">
          {client.name ? (
            <span className="truncate text-sm font-medium">{client.name}</span>
          ) : (
            <MockupSkeletonLine className="w-28" />
          )}
          {client.businessNature && (
            <span className="truncate text-xs text-muted-foreground">
              {businessNatureCategoryLabel(client.businessNature.category)}
              {client.businessNature.description ? ` - ${client.businessNature.description}` : ""}
            </span>
          )}
          <div className="flex gap-1.5">
            {client.services.length > 0 ? (
              client.services.map((service) => (
                <span
                  key={service}
                  className={cn(
                    "rounded-full border px-2.5 py-1 text-[11px] font-medium",
                    isLive ? "border-primary/30 bg-primary/10 text-primary" : "bg-muted/40 text-muted-foreground"
                  )}
                >
                  {SERVICE_MODULE_LABEL[service]}
                </span>
              ))
            ) : (
              <>
                <MockupSkeletonPill className="w-14" />
                <MockupSkeletonPill className="w-20" />
              </>
            )}
          </div>
          {showPlanTag && <MockupSkeletonPill className="w-16" />}
        </div>

        <div className="flex items-center gap-2 border-b pb-3">
          {connectReached ? (
            <>
              <MockupStatusDot active={connected} />
              <span className="text-xs">{booksLabel}</span>
              {/* Named with the company the books reported, not the client name
                  we were given. They are usually the same, and when they are
                  not, the one that proves we joined the right books is the one
                  worth showing. */}
              <span className="truncate text-xs text-muted-foreground">
                {connected
                  ? `Connected to ${connectedTo || client.name || "this client"}`
                  : "Not yet connected"}
              </span>
            </>
          ) : (
            <>
              <MockupSkeletonCircle className="size-3" />
              <MockupSkeletonLine className="w-24" />
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1.5 border-b pb-3">
          {/* What Confirm connection read out of the books, as soon as it has
              read it. This filled in only once someone reached the review
              screens, which is a step after the facts are already known — and
              the preview exists to show the workspace forming, not to lag it. */}
          {readGstins > 0 || readBanks > 0 ? (
            <>
              {readGstins > 0 && (
                <span className="rounded-full border bg-muted/40 px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                  {gstinText && readGstins === 1
                    ? gstinText
                    : `${readGstins} GSTIN${readGstins === 1 ? "" : "s"}`}
                </span>
              )}
              {readBanks > 0 && (
                <span className="rounded-full border bg-muted/40 px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                  {readBanks} bank account{readBanks === 1 ? "" : "s"}
                </span>
              )}
            </>
          ) : !gstinsOrBanksReached ? (
            <>
              <MockupSkeletonPill className="w-20" />
              <MockupSkeletonPill className="w-24" />
            </>
          ) : (
            <span className="text-[11px] text-muted-foreground">Nothing confirmed yet.</span>
          )}
        </div>

        <div className="flex flex-col gap-1.5 border-b pb-3">
          <span className="text-[11px] font-medium text-muted-foreground">Who has access</span>
          {/* Written by the team-assignment screen after every roster change,
              the ZOHO_CONNECTION_KEY pattern: the preview reads the answer
              rather than every screen re-reading the roster. */}
          {teamPreview && teamPreview.length > 0 ? (
            teamPreview.map((entry) => {
              const parts = entry.name.trim().split(/\s+/).filter(Boolean);
              const initials =
                parts.length === 0
                  ? "?"
                  : parts.length === 1
                    ? parts[0].slice(0, 2)
                    : `${parts[0][0]}${parts[parts.length - 1][0]}`;
              return (
                <div key={entry.name} className="flex items-center gap-2">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-[9px] font-medium text-muted-foreground">
                    {initials.toUpperCase()}
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-[11px] font-medium">{entry.name}</span>
                    <span className="truncate text-[10px] text-muted-foreground">{entry.line}</span>
                  </div>
                  <span className="shrink-0 rounded-full border bg-muted/40 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                    {entry.roleLabel}
                  </span>
                </div>
              );
            })
          ) : teamReached ? (
            <span className="text-[11px] text-muted-foreground">No users assigned yet.</span>
          ) : (
            <div className="flex items-center gap-2">
              <MockupSkeletonCircle />
              <MockupSkeletonCircle />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1 border-b pb-3">
          <span className="text-[11px] font-medium text-muted-foreground">What waits for review</span>
          {/* Written by the team screen after every seats/policy change — the
              teamAccess pattern again: the preview reads the answer rather
              than re-resolving the tabs. Legacy count keys, three-state
              meanings: not_allowed carries Blocked (Checker-only). */}
          {approvalsPreview && approvalsPreview.chosen ? (
            <>
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-muted-foreground">Policies</span>
                <span className="text-[11px] font-medium">{approvalsPreview.packageLabel}</span>
              </div>
              {(
                [
                  ["Post Directly", approvalsPreview.counts.posts_directly],
                  ["Send for Review", approvalsPreview.counts.needs_approval],
                  ["Blocked", approvalsPreview.counts.not_allowed],
                ] as const
              ).map(([label, count]) => (
                <div key={label} className="flex items-center justify-between">
                  <span className="text-[11px] text-muted-foreground">{label}</span>
                  <span className="text-[11px] font-medium tabular-nums">{count}</span>
                </div>
              ))}
            </>
          ) : teamReached ? (
            <span className="text-[11px] text-muted-foreground">No rules chosen yet.</span>
          ) : (
            <MockupSkeletonLine className="w-24" />
          )}
        </div>

        <div className="flex flex-col gap-1 border-b pb-3">
          <span className="text-[11px] font-medium text-muted-foreground">Escalation contacts</span>
          {/* Written by the escalations & access screen after every change —
              the teamAccess pattern again. Escalation names in the order
              go-live pushes them; the dashboard list is just a count. */}
          {escalationsPreview && escalationsPreview.escalation.length > 0 ? (
            <>
              {escalationsPreview.escalation.map((name, index) => (
                <div key={`${index}-${name}`} className="flex items-center gap-2">
                  <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-muted text-[9px] font-medium text-muted-foreground">
                    {index + 1}
                  </span>
                  <span className="truncate text-[11px] font-medium">{name}</span>
                </div>
              ))}
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-muted-foreground">Dashboard access</span>
                <span className="text-[11px] font-medium tabular-nums">{escalationsPreview.dashboardCount}</span>
              </div>
            </>
          ) : teamReached ? (
            <span className="text-[11px] text-muted-foreground">No escalation contacts yet.</span>
          ) : (
            <MockupSkeletonLine className="w-24" />
          )}
        </div>

        <div className="flex items-center gap-2">
          {isLive ? (
            <>
              <span className="size-2 shrink-0 rounded-full bg-primary" />
              <span className="text-xs font-medium">Live</span>
            </>
          ) : goLiveReached ? (
            <>
              <MockupStatusDot active={false} />
              <span className="text-xs text-muted-foreground">Setting up</span>
            </>
          ) : (
            <MockupSkeletonLine className="w-20" />
          )}
        </div>
      </BrowserMockup>
    </aside>
  );
}

export function OnboardingLayout({
  clientId,
  activeSlug,
  children,
}: {
  clientId: ClientId;
  activeSlug: string | undefined;
  children: ReactNode;
}) {
  const client = useCoClient(clientId);

  if (!client) {
    return (
      <div className="co-root flex h-screen flex-col items-center justify-center gap-2 p-16 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">Client not found</h1>
        <p className="text-sm text-muted-foreground">This client doesn't exist in this session.</p>
      </div>
    );
  }

  const stepGroup = activeSlug ? findMainStepForSlug(client, activeSlug) : undefined;
  const showConnectorNotice =
    client.backend.system === "tally" &&
    (activeSlug === "connect-tally" || stepGroup?.id === "verify-setup");

  return (
    <div className="co-root flex h-screen overflow-hidden text-left">
      <LeftNav clientId={clientId} activeSlug={activeSlug} />
      <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {showConnectorNotice && (
          <div className="px-10 pt-6">
            <div className="mx-auto w-full max-w-2xl">
              <Callout>
                Keep the Zournal Tally connector open, and leave this company open in Tally, until this setup is
                finished. Ledgers, taxes, banks, and parties are created on that computer.
              </Callout>
            </div>
          </div>
        )}
        <div className={cn("min-h-0 flex-1 overflow-y-auto px-10 pb-10", showConnectorNotice ? "pt-4" : "pt-10")}>
          <div className="mx-auto flex w-full max-w-2xl flex-col gap-2">{children}</div>
        </div>
      </main>
      <RightPreview clientId={clientId} />
    </div>
  );
}
